import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getSession = () => {
  let s = localStorage.getItem("elm_chat_session");
  if (!s) { s = `web-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`; localStorage.setItem("elm_chat_session", s); }
  return s;
};

export const streamSSE = async (url, body, onToken, headers = {}) => {
  const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json", ...headers }, body: JSON.stringify(body), credentials: "include" });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.replace(/^data: /, "");
      if (line === "[DONE]") return;
      try { onToken(JSON.parse(line).t); } catch { /* ignore */ }
    }
  }
};

const Linkify = ({ text }) => {
  const parts = text.split(/(\/(?:offres\/[a-z-]+|boutique|agenda(?:\?type=voyage)?|contact|faq|carte-cadeau|a-propos|blog)\b(?:#[a-z-]+)?)/g);
  return parts.map((p, i) => (p.startsWith("/") ? <Link key={i} to={p} className="text-terra underline underline-offset-2">{p}</Link> : <span key={i}>{p}</span>));
};

const SUGGESTIONS = ["Quels sont les prochains stages ?", "Comment se passe une consultation à distance ?", "Combien coûte une harmonisation de lieu ?"];

export const AssistantBubble = () => {
  const { pathname } = useLocation();
  const stagePage = ["/offres/stage-fabrication-tambour", "/creer-son-tambour"].includes(pathname);
  const structuresPage = pathname === "/tambour-pour-structures";
  const adminPage = pathname.startsWith("/admin");
  const [open, setOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const session = useRef(null);

  useEffect(() => {
    const verifier = () => setBookingOpen(!!document.querySelector('[data-testid="booking-dialog"][data-state="open"]'));
    verifier();
    const observer = new MutationObserver(verifier);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => { if (bookingOpen) setOpen(false); }, [bookingOpen]);

  useEffect(() => {
    if (!open) return;
    session.current = getSession();
    fetch(`${API}/assistant/chat/${session.current}`).then((r) => r.json()).then((h) => setMsgs(h.map((m) => ({ role: m.role, texte: m.texte })))).catch(() => {});
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (texte) => {
    const message = (texte ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", texte: message }, { role: "assistant", texte: "" }]);
    try {
      await streamSSE(`${API}/assistant/chat`, { session_id: session.current, message }, (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], texte: c[c.length - 1].texte + t }; return c; }));
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", texte: "Je n'arrive pas à répondre pour le moment. Écrivez-nous via /contact." }; return c; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!bookingOpen && <button onClick={() => setOpen((o) => !o)} data-testid="assistant-toggle" aria-label="Poser une question à l'assistant"
        className={`fixed ${stagePage ? "bottom-24 lg:bottom-6" : "bottom-6"} right-6 z-[55] w-14 h-14 rounded-full bg-earth text-sand shadow-2xl shadow-earth/30 ${adminPage ? "hidden" : structuresPage ? "hidden sm:flex" : "flex"} items-center justify-center hover:bg-terra hover:text-earth transition-colors duration-300`}>
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>}
      <AnimatePresence>
        {open && !bookingOpen && (
          <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }} transition={{ duration: 0.25 }}
            className={`fixed ${stagePage ? "bottom-40 lg:bottom-24" : "bottom-24"} right-6 z-[55] w-[min(24rem,calc(100vw-3rem))] h-[min(34rem,calc(100vh-8rem))] bg-sand border border-sand-dark rounded-2xl shadow-2xl shadow-earth/20 ${adminPage ? "hidden" : structuresPage ? "hidden sm:flex" : "flex"} flex-col overflow-hidden`} data-testid="assistant-panel">
            <div className="px-5 py-4 bg-earth text-sand">
              <p className="font-serif text-lg leading-tight">Une question ?</p>
              <p className="text-[11px] text-sand/60 mt-0.5">Assistant d'Entre Les Mondes — services, tarifs, dates. Réponses indicatives.</p>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm" data-testid="assistant-messages">
              {msgs.length === 0 && (
                <div className="space-y-2">
                  <p className="text-fumee">Bonjour ! Posez votre question ou choisissez un sujet :</p>
                  {SUGGESTIONS.map((s, i) => (
                    <button key={s} onClick={() => send(s)} data-testid={`assistant-suggestion-${i}`} className="block w-full text-left px-3 py-2 rounded-xl border border-wood/20 text-bark hover:border-terra hover:text-terra transition-colors">{s}</button>
                  ))}
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`max-w-[88%] px-3.5 py-2.5 rounded-2xl leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-terra text-earth" : "bg-card border border-sand-dark text-bark"}`} data-testid={`assistant-msg-${m.role}`}>
                  {m.role === "assistant" ? (m.texte ? <Linkify text={m.texte} /> : <Loader2 className="w-4 h-4 animate-spin text-terra" />) : m.texte}
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-sand-dark flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Votre question…" data-testid="assistant-input"
                className="flex-1 bg-white/60 border border-wood/25 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-terra" />
              <button type="submit" disabled={busy || !input.trim()} data-testid="assistant-send" aria-label="Envoyer"
                className="w-10 h-10 rounded-full bg-earth text-sand flex items-center justify-center hover:bg-terra hover:text-earth transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
